import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Building2, Globe, Crown, GitBranch, Star } from 'lucide-react';
import { Company } from '@/data/mockUsers';
import { deleteCompany } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const MASTER_TENANT_ID = '00000000-0000-0000-0000-000000000001';

interface CompanyCardProps {
  company: Company;
  onDelete?: () => void;
}

export function CompanyCard({ company, onDelete }: CompanyCardProps) {
  const isWhiteLabel = company.tenant_id && company.tenant_id !== MASTER_TENANT_ID;
  // Main Company = the tenant's primary company (root of the company tree).
  // Branch = sub-company under a main company.
  // root_company_id is null OR equal to self.id for the main company; for
  // branches it points at the parent's id.
  const isBranch = !!(company.root_company_id && company.root_company_id !== company.id);
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const industryColors = {
    pharmaceutical: 'bg-blue-50 border-blue-200 text-blue-700',
    hospital: 'bg-green-50 border-green-200 text-green-700',
    research: 'bg-purple-50 border-purple-200 text-purple-700',
    medtech: 'bg-orange-50 border-orange-200 text-orange-700',
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteCompany(company.id);
      setShowDeleteDialog(false);
      onDelete?.();
    } catch (error) {
      // Error already handled in deleteCompany
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-card border-2 border-border rounded-xl overflow-hidden hover:shadow-lg transition-all group">
        {/* Banner / hero */}
        <div className="relative h-28 w-full overflow-hidden">
          {company.banner_image_url ? (
            <img
              src={company.banner_image_url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/85 to-primary/55" />
          )}
          {/* soft highlight + bottom scrim for depth */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_60%)]" />
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/25 to-transparent" />
        </div>

        {/* Header with Logo */}
        <div className="p-6 border-b border-border bg-gradient-to-br from-muted/50 to-background">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {company.logo ? (
                <img src={company.logo} alt={company.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-primary" size={24} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-foreground truncate">{company.name}</h3>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {company.industry && (
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${
                      industryColors[company.industry] || 'bg-muted border-border text-foreground'
                    }`}>
                      {company.industry.charAt(0).toUpperCase() + company.industry.slice(1)}
                    </span>
                  )}
                  {isWhiteLabel && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-amber-50 border-amber-200 text-amber-700">
                      <Crown size={12} />
                      White Label
                    </span>
                  )}
                  {isBranch ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-sky-50 border-sky-200 text-sky-700">
                      <GitBranch size={12} />
                      Branch
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-emerald-50 border-emerald-200 text-emerald-700">
                      <Star size={12} />
                      Main Company
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0 ml-2">
              <button
                onClick={() => navigate(`/admin/companies/${company.id}/edit`)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                title="Edit company"
              >
                <Edit2 size={16} className="text-muted-foreground" />
              </button>
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                title="Delete company"
              >
                <Trash2 size={16} className="text-destructive" />
              </button>
            </div>
          </div>

          {/* Website */}
          {company.website && (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Globe size={14} />
              {company.website.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>

        {/* Stats Grid */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-foreground">{company.total_users}</div>
              <div className="text-xs text-muted-foreground">Total Users</div>
              <div className="text-xs text-green-600 mt-1">{company.active_users} active</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{company.total_simulations}</div>
              <div className="text-xs text-muted-foreground">Simulations</div>
            </div>
          </div>

          {/* Team Score */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Team Average</span>
              <span className="text-lg font-bold text-primary">{company.avg_team_score}/100</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${company.avg_team_score}%` }}
              />
            </div>
          </div>

          {/* Status & Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            {company.status && (
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                company.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  company.status === 'active' ? 'bg-green-500' : 'bg-muted-foreground'
                }`} />
                {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
              </span>
            )}
            <span className="text-xs text-muted-foreground">Since {company.created_at}</span>
          </div>

          {/* View Details Button */}
          <button
            onClick={() => navigate(`/admin/companies/${company.id}`)}
            className="w-full mt-4 py-2 border border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            View Details
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{company.name}</strong>? This action cannot be undone.
              <br /><br />
              <span className="text-destructive">Note: Companies with assigned users or agents cannot be deleted.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete Company'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
